import * as CL from '@design-system/component-library';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import {
  customerNumberMsg,
  deliveryMethodMsg,
  eInvoiceMsg,
  invoiceLanguageMsg,
  invoicingDetailsMsg,
  t,
} from '../../../common/i18n/index.js';
import { ElectronicAddress } from '../../../common/react-hook-form/fields/ElectronicAddress';
import { ElectronicOperator } from '../../../common/react-hook-form/fields/ElectronicOperator';
import { LanguageDropdown } from '../../../common/react-hook-form/fields/LanguageDropdown.js';
import { TextInput } from '../../../common/react-hook-form/fields/TextInput.js';
import { getDefaultElectronicOperator } from '../../../common/utils/billingAccountUtils.js';
import { getLanguageDisplayValue } from '../../../common/utils/languageUtils.js';
import { validateCustomerNumber } from './utils.js';
import type { EOperatorChangeRequestFormValues, FormEditProps } from './EOperatorChangeRequestForm.js';
import type { EOperatorItem } from '../../../common/utils/billingAccountUtils.js';
import type { BillChannel } from '../../../generated/api/billChannel';
import type { ContactSupportFormValues } from '../ContactSupportForm';

const BILL_LANGUAGE_FIELD_NAME = 'billLanguage';
const EOPERATOR_FIELD_NAME = 'eOperator';
const EADDRESS_FIELD_NAME = 'eInvoiceAddress';

interface BillingDetailsProps {
  requireCustomerNumber: boolean;
  billChannels: BillChannel[];
}

export const BillingDetails = ({
  requireCustomerNumber,
  isEditing = true,
  billChannels,
}: BillingDetailsProps & FormEditProps) => {
  const { getValues, setValue, setError } = useFormContext<
    ContactSupportFormValues & EOperatorChangeRequestFormValues
  >();

  useEffect(() => {
    const defaultOperator = getDefaultElectronicOperator(billChannels);
    const defaultElectronicAddress =
      defaultOperator?.active && defaultOperator?.electronicAddress ? defaultOperator.electronicAddress : '';
    const currentOperator = getValues(EOPERATOR_FIELD_NAME);
    const currentAddress = getValues(EADDRESS_FIELD_NAME);

    if (currentAddress !== defaultElectronicAddress || currentOperator !== defaultOperator?.id) {
      setValue(EOPERATOR_FIELD_NAME, defaultOperator?.id || '');
      setValue(EADDRESS_FIELD_NAME, defaultElectronicAddress);
    }
  }, [setValue, billChannels, getValues]);

  const handleOperatorChange = (selectedOperator?: EOperatorItem) => {
    if (selectedOperator?.active && selectedOperator?.electronicAddress) {
      setValue(EADDRESS_FIELD_NAME, selectedOperator.electronicAddress);
    } else {
      setValue(EADDRESS_FIELD_NAME, '');
    }

    setError(EADDRESS_FIELD_NAME, { message: undefined });
  };

  return (
    <>
      <h4>{t.RPMR(invoicingDetailsMsg)}</h4>
      <CL.Grid>
        {requireCustomerNumber && (
          <CL.GridRow>
            <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
              <TextInput
                name="customerNumber"
                label={t.A00L(customerNumberMsg)}
                tooltip={t.A00N(
                  'You can find your customer number on your latest invoice. If your company has more than one customer number, you can add any of them.'
                )}
                validate={validateCustomerNumber}
                readonly={!isEditing}
              />
            </CL.GridCol>
          </CL.GridRow>
        )}
        <CL.GridRow>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <TextInput
              name="deliveryMethod"
              label={t.G0QN(deliveryMethodMsg)}
              placeholder={t.IK1D(eInvoiceMsg)}
              disabled={isEditing}
              readonly={!isEditing}
            />
          </CL.GridCol>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            {isEditing ? (
              <LanguageDropdown name={BILL_LANGUAGE_FIELD_NAME} label={t.A7DR(invoiceLanguageMsg)} />
            ) : (
              <div className={dsClass.INPUT}>
                <div className={dsClass.INPUT_LABELAREA}>
                  <label className={dsClass.INPUT_LABELAREA_LABEL}>{t.A7DR(invoiceLanguageMsg)}</label>
                </div>
                <div className={dsClass.INPUT_INPUTAREA}>
                  {getLanguageDisplayValue(getValues(BILL_LANGUAGE_FIELD_NAME))}
                </div>
              </div>
            )}
          </CL.GridCol>
        </CL.GridRow>
        <CL.GridRow>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <ElectronicOperator
              name={EOPERATOR_FIELD_NAME}
              billChannels={billChannels}
              readonly={!isEditing}
              onOperatorChange={handleOperatorChange}
            />
          </CL.GridCol>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <ElectronicAddress
              name={EADDRESS_FIELD_NAME}
              required={true}
              readonly={!isEditing}
              hint={t.VXVC('EDI identifier')}
            />
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
    </>
  );
};
